/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetTipiDocumentoParams,
  PostTipiDocumentoBody,
  PutTipiDocumentoIdBody,
  TipoDocumentoDto,
  WsProblema,
  WsRispostaPaginataListTipoDocumentoDto,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene i dettagli di un tipo domanda.
 * @summary Dettaglio tipo domanda
 */
export const getTipiDocumentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoDocumentoDto>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiDocumentoIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-documento/${id}`] as const;

export const getGetTipiDocumentoIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiDocumentoId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiDocumentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiDocumentoIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiDocumentoId>>
  > = ({ signal }) => getTipiDocumentoId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiDocumentoIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiDocumentoId>>
>;
export type GetTipiDocumentoIdQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio tipo domanda
 */
export const useGetTipiDocumentoId = <
  TData = Awaited<ReturnType<typeof getTipiDocumentoId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiDocumentoIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna il Tipo Documento.
 * @summary Aggiorna il Tipo Documento
 */
export const putTipiDocumentoId = (
  id: string,
  putTipiDocumentoIdBody: PutTipiDocumentoIdBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  if (putTipiDocumentoIdBody.fileFinale !== undefined) {
    formData.append('fileFinale', putTipiDocumentoIdBody.fileFinale);
  }
  if (putTipiDocumentoIdBody.fileIntermedio !== undefined) {
    formData.append('fileIntermedio', putTipiDocumentoIdBody.fileIntermedio);
  }
  if (putTipiDocumentoIdBody.tipoDocumento !== undefined) {
    formData.append('tipoDocumento', putTipiDocumentoIdBody.tipoDocumento);
  }

  return apiClientRequest<TipoDocumentoDto>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPutTipiDocumentoIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiDocumentoId>>,
    TError,
    { id: string; data: PutTipiDocumentoIdBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiDocumentoId>>,
  TError,
  { id: string; data: PutTipiDocumentoIdBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiDocumentoId>>,
    { id: string; data: PutTipiDocumentoIdBody }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiDocumentoId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiDocumentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiDocumentoId>>
>;
export type PutTipiDocumentoIdMutationBody = PutTipiDocumentoIdBody;
export type PutTipiDocumentoIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Aggiorna il Tipo Documento
 */
export const usePutTipiDocumentoId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiDocumentoId>>,
    TError,
    { id: string; data: PutTipiDocumentoIdBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiDocumentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Elimina un tipo documento.
 * @summary Elimina tipo documento
 */
export const deleteTipiDocumentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiDocumentoIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiDocumentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiDocumentoId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiDocumentoId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipiDocumentoId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiDocumentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiDocumentoId>>
>;

export type DeleteTipiDocumentoIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina tipo documento
 */
export const useDeleteTipiDocumentoId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiDocumentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTipiDocumentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna l'elenco dei tipi documento.
 * @summary Elenco tipi Documento
 */
export const getTipiDocumento = (
  params?: GetTipiDocumentoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoDocumentoDto>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiDocumentoQueryKey = (params?: GetTipiDocumentoParams) =>
  [
    `/framework-cfg-procedimenti/tipi-documento`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiDocumentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiDocumento>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiDocumentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiDocumento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiDocumentoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiDocumento>>
  > = ({ signal }) => getTipiDocumento(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiDocumentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiDocumento>>
>;
export type GetTipiDocumentoQueryError = ErrorType<unknown>;

/**
 * @summary Elenco tipi Documento
 */
export const useGetTipiDocumento = <
  TData = Awaited<ReturnType<typeof getTipiDocumento>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiDocumentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiDocumentoQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo Tipo Documento.
 * @summary Crea Tipo Documento
 */
export const postTipiDocumento = (
  postTipiDocumentoBody: PostTipiDocumentoBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  if (postTipiDocumentoBody.fileFinale !== undefined) {
    formData.append('fileFinale', postTipiDocumentoBody.fileFinale);
  }
  if (postTipiDocumentoBody.fileIntermedio !== undefined) {
    formData.append('fileIntermedio', postTipiDocumentoBody.fileIntermedio);
  }
  if (postTipiDocumentoBody.tipoDocumento !== undefined) {
    formData.append('tipoDocumento', postTipiDocumentoBody.tipoDocumento);
  }

  return apiClientRequest<TipoDocumentoDto>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostTipiDocumentoMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiDocumento>>,
    TError,
    { data: PostTipiDocumentoBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiDocumento>>,
  TError,
  { data: PostTipiDocumentoBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiDocumento>>,
    { data: PostTipiDocumentoBody }
  > = props => {
    const { data } = props ?? {};

    return postTipiDocumento(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiDocumentoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiDocumento>>
>;
export type PostTipiDocumentoMutationBody = PostTipiDocumentoBody;
export type PostTipiDocumentoMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Tipo Documento
 */
export const usePostTipiDocumento = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiDocumento>>,
    TError,
    { data: PostTipiDocumentoBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiDocumentoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Scarica il template finale
 * @summary Scarica il template finale
 */
export const getTipiDocumentoIdTemplateIntermedio = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento/${id}/templateIntermedio`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiDocumentoIdTemplateIntermedioQueryKey = (id: string) =>
  [
    `/framework-cfg-procedimenti/tipi-documento/${id}/templateIntermedio`,
  ] as const;

export const getGetTipiDocumentoIdTemplateIntermedioQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTipiDocumentoIdTemplateIntermedioQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>
  > = ({ signal }) =>
    getTipiDocumentoIdTemplateIntermedio(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiDocumentoIdTemplateIntermedioQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>
>;
export type GetTipiDocumentoIdTemplateIntermedioQueryError =
  ErrorType<WsProblema>;

/**
 * @summary Scarica il template finale
 */
export const useGetTipiDocumentoIdTemplateIntermedio = <
  TData = Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumentoIdTemplateIntermedio>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiDocumentoIdTemplateIntermedioQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Scarica il template finale
 * @summary Scarica il template finale
 */
export const getTipiDocumentoIdTemplateFinale = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cfg-procedimenti/tipi-documento/${id}/templateFinale`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiDocumentoIdTemplateFinaleQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-documento/${id}/templateFinale`] as const;

export const getGetTipiDocumentoIdTemplateFinaleQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiDocumentoIdTemplateFinaleQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>
  > = ({ signal }) =>
    getTipiDocumentoIdTemplateFinale(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiDocumentoIdTemplateFinaleQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>
>;
export type GetTipiDocumentoIdTemplateFinaleQueryError = ErrorType<WsProblema>;

/**
 * @summary Scarica il template finale
 */
export const useGetTipiDocumentoIdTemplateFinale = <
  TData = Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiDocumentoIdTemplateFinale>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiDocumentoIdTemplateFinaleQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
